import { artworkUrl } from '@shared/assets/artwork';
import { registerBlockDefinition } from '@shared/block-editor';
import { registerBlockRenderer, type BlockNode } from '@shared/block-renderer';

/**
 * The v1 editorial block set — **seven types, and the pack fixes both the list
 * and its order**: فقرة · عنوان فرعي · قائمة نقطية · اقتباس · ملاحظة · صورة ·
 * مقطع مرئي. It is the slash menu, the add-at-the-end row, and the shapes the
 * reader's renderer draws, and those three are one list rather than three.
 *
 * **Editor and renderer are registered together, in this module and this MR.**
 * A type registered on one side only is the contract-drift the parity rule
 * exists to catch: a block a writer can insert and a reader cannot see, or a
 * shape stored bodies carry and this build silently drops.
 *
 * Media are **islands**: `image` and `video` are not text, and the flow marks
 * them `contenteditable="false"` so a keystroke in the wrong place cannot take
 * a card apart. Their caption is a real `<input>` inside the island, which is
 * why it stays writable while the card around it does not.
 *
 * No renderer uses `dangerouslySetInnerHTML`. The stored value of a text block
 * is a string and it is rendered as a string.
 */

export const EDITORIAL_BLOCK_TYPES = ['p', 'h3', 'ul', 'quote', 'note', 'image', 'video'] as const;

export type EditorialBlockType = (typeof EDITORIAL_BLOCK_TYPES)[number];

/** Labels are the pack's, verbatim; they name the type in the menu and the row. */
export const BLOCK_LABELS: Readonly<Record<EditorialBlockType, string>> = {
  p: 'فقرة',
  h3: 'عنوان فرعي',
  ul: 'قائمة نقطية',
  quote: 'اقتباس',
  note: 'ملاحظة',
  image: 'صورة',
  video: 'مقطع مرئي',
};

/** The empty node each type starts as. */
const EMPTY_DATA: Readonly<Record<EditorialBlockType, () => Record<string, unknown>>> = {
  p: () => ({ text: '' }),
  h3: () => ({ text: '' }),
  ul: () => ({ items: [''] }),
  quote: () => ({ text: '', cite: '' }),
  note: () => ({ text: '' }),
  image: () => ({ url: null, caption: '' }),
  video: () => ({ url: null, poster: null, caption: '' }),
};

export function createBlock(type: EditorialBlockType, id: string): BlockNode {
  return { id, type, data: EMPTY_DATA[type]() };
}

function text(block: BlockNode): string {
  return typeof block.data.text === 'string' ? block.data.text : '';
}

function items(block: BlockNode): string[] {
  return Array.isArray(block.data.items) ? block.data.items.filter((item) => typeof item === 'string') : [];
}

function caption(block: BlockNode): string {
  return typeof block.data.caption === 'string' ? block.data.caption : '';
}

function url(block: BlockNode): string | null {
  return typeof block.data.url === 'string' ? block.data.url : null;
}

/**
 * The placeholder surface every media block falls back to.
 *
 * A post is authored before its artwork lands, and an `<img>` with no `src` is
 * a broken-image glyph the operator has to interpret. The gradient panel is the
 * same one the pack draws, and it carries the play mark on a video so the two
 * kinds are told apart without reading the caption.
 */
function MediaSurface({ src, alt, play }: { src: string | null; alt: string; play?: boolean }) {
  // Joined to the asset origin here rather than at the three block renderers:
  // a block's `url` is the same root-relative reference every other artwork
  // read states, and this host cannot serve one.
  const resolved = artworkUrl(src);

  if (resolved) {
    return (
      <img src={resolved} alt={alt} className="block w-full rounded-sm border border-border object-cover" />
    );
  }

  return (
    <span
      aria-hidden="true"
      className="grid aspect-video w-full place-items-center rounded-sm border border-border bg-[linear-gradient(145deg,color-mix(in_srgb,var(--brand-noir)_72%,var(--brand-bone)_28%),color-mix(in_srgb,var(--brand-noir)_90%,var(--brand-bone)_10%)_55%,color-mix(in_srgb,var(--brand-noir)_80%,var(--brand-indigo)_20%))] text-[var(--brand-bone)]"
    >
      {play ? (
        <svg viewBox="0 0 24 24" fill="currentColor" className="size-10 opacity-85" aria-hidden="true">
          <path d="M8 5v14l11-7z" />
        </svg>
      ) : null}
    </span>
  );
}

let registered = false;

/**
 * Registration is idempotent and runs from the feature's route module, so the
 * renderer is populated before any surface of this feature mounts — and so a
 * route the operator never opens costs nothing.
 */
export function registerEditorialBlocks(): void {
  if (registered) return;
  registered = true;

  for (const type of EDITORIAL_BLOCK_TYPES) {
    registerBlockDefinition({
      type,
      label: BLOCK_LABELS[type],
      create: (id) => createBlock(type, id),
    });
  }

  registerBlockRenderer('p', (block) => <p className="mb-0">{text(block)}</p>);

  registerBlockRenderer('h3', (block) => (
    <h3 className="mb-0 mt-6 font-display text-h3 font-semibold">{text(block)}</h3>
  ));

  registerBlockRenderer('ul', (block) => (
    <ul className="m-0 list-disc ps-5">
      {items(block).map((item, index) => (
        <li key={index} className="mb-2 last:mb-0">
          {item}
        </li>
      ))}
    </ul>
  ));

  registerBlockRenderer('quote', (block) => (
    <blockquote className="m-0 border-s-2 border-border-strong ps-4 text-ink-muted">
      {text(block)}
      {typeof block.data.cite === 'string' && block.data.cite ? (
        // The rule at the reading-start edge mirrors with the document on its
        // own; a drawn quotation mark would need flipping by hand.
        <cite className="mt-2 block text-sm not-italic">{block.data.cite}</cite>
      ) : null}
    </blockquote>
  ));

  registerBlockRenderer('note', (block) => (
    // The note takes the notice colour because it is a notice, and the design
    // system makes `--info` and the editorial indigo one token deliberately.
    <aside className="rounded-sm border-s-[3px] border-info bg-info-soft p-4 text-sm">{text(block)}</aside>
  ));

  registerBlockRenderer('image', (block) => (
    <figure className="m-0">
      <MediaSurface src={url(block)} alt={caption(block)} />
      {caption(block) ? (
        <figcaption className="mt-2 text-sm text-ink-muted">{caption(block)}</figcaption>
      ) : null}
    </figure>
  ));

  registerBlockRenderer('video', (block) => (
    <figure className="m-0">
      <MediaSurface
        src={typeof block.data.poster === 'string' ? block.data.poster : null}
        alt={caption(block)}
        play
      />
      {caption(block) ? (
        <figcaption className="mt-2 text-sm text-ink-muted">{caption(block)}</figcaption>
      ) : null}
    </figure>
  ));
}
